<template>
  <div class="vue-query-builder" :class="{ 'vue-query-builder-styled': styled }">
    <query-builder-group
      :index="0"
      :query.sync="query"
      :ruleTypes="ruleTypes"
      :rules="mergedRules"
      :maxDepth="maxDepth"
      :depth="depth"
      :styled="styled"
      :labels="mergedLabels"
      type="query-builder-group"
    ></query-builder-group>
  </div>
</template>

<script>
import QueryBuilderGroup from "./components/QueryBuilderGroup.vue";
import deepClone from "./utilities.js";

var defaultLabels = {
  matchType: "Match Type",
  matchTypes: [{ id: "all", label: "All" }, { id: "any", label: "Any" }],
  addRule: "Add Rule",
  removeRule: "&times;",
  addGroup: "Add Group",
  removeGroup: "&times;",
  textInputPlaceholder: "value"
};

export default {
  name: "vue-query-builder",

  components: {
    QueryBuilderGroup
  },

  props: {
    rules: Array,
    labels: {
      type: Object,
      default() {
        return defaultLabels;
      }
    },
    styled: {
      type: Boolean,
      default: true
    },
    maxDepth: {
      type: Number,
      default: 10,
      validator: function(value) {
        return value >= 1;
      }
    },
    value: Object
  },

  data() {
    return {
      depth: 1,
      query: {
        logicalOperator: this.labels.matchTypes[0].id,
        children: []
      },
      ruleTypes: {
        text: {
          operators: [
            "equals",
            "does not equal",
            "contains",
            "does not contain",
            "is empty",
            "is not empty",
            "begins with",
            "ends with"
          ],
          inputType: "text",
          id: "text-field"
        },
        numeric: {
          operators: ["==", "<>", "<", "<=", ">", ">="],
          inputType: "number",
          id: "number-field"
        },
        suggest: {
          operators: ["==", "<>", "<", "<=", ">", ">="],
          choices: [],
          inputType: "suggest",
          id: "suggest-field"
        },
        date: {
          operators: [
            "equals",
            "does not equal",
            "contains",
            "does not contain",
            "is empty",
            "is not empty",
            "begins with",
            "ends with"
          ],
          inputType: "date",
          id: "date-field"
        },
        custom: {
          operators: [],
          inputType: "text",
          id: "custom-field"
        },
        radio: {
          operators: [],
          choices: [],
          inputType: "radio",
          id: "radio-field"
        },
        checkbox: {
          operators: [],
          choices: [],
          inputType: "checkbox",
          id: "checkbox-field"
        },
        select: {
          operators: [],
          choices: [],
          inputType: "select",
          id: "select-field"
        },
        "multi-select": {
          operators: ["="],
          choices: [],
          inputType: "select",
          id: "multi-select-field"
        }
      }
    };
  },

  computed: {
    mergedLabels() {
      return Object.assign({}, defaultLabels, this.labels);
    },

    mergedRules() {
      var mergedRules = [];
      var vm = this;

      vm.rules.forEach(function(rule) {
        if (typeof vm.ruleTypes[rule.type] !== "undefined") {
          mergedRules.push(Object.assign({}, vm.ruleTypes[rule.type], rule));
        } else {
          mergedRules.push(rule);
        }
      });

      return mergedRules;
    }
  },

  mounted() {
    this.$watch(
      "query",
      newQuery => {
        if (JSON.stringify(newQuery) !== JSON.stringify(this.value)) {
          this.$emit("input", deepClone(newQuery));
        }
      },
      {
        deep: true
      }
    );

    this.$watch(
      "value",
      newValue => {
        if (JSON.stringify(newValue) !== JSON.stringify(this.query)) {
          this.query = deepClone(newValue);
        }
      },
      {
        deep: true
      }
    );

    if (typeof this.$options.propsData.value !== "undefined") {
      this.query = Object.assign(this.query, this.$options.propsData.value);
    }
  }
};
</script>

<style>
.vue-query-builder-styled.vue-query-builder {
  border: 1px solid #ccc;
}
.vue-query-builder-styled.vue-query-builder > .vqb-group::after,
.vue-query-builder-styled.vue-query-builder > .vqb-group::before {
  border: 0;
}

.vue-query-builder-styled .vqb-group-body {
  margin-left: 25px;
}

.vue-query-builder-styled .vqb-rule,
.vue-query-builder-styled .vqb-group {
  position: relative;
}

.vue-query-builder-styled .vqb-rule::after,
.vue-query-builder-styled .vqb-rule::before,
.vue-query-builder-styled .vqb-group::after,
.vue-query-builder-styled .vqb-group::before {
  border-style: solid;
  content: "";
  left: -12px;
  position: absolute;
  width: 10px;
}

.vue-query-builder-styled .vqb-rule::before,
.vue-query-builder-styled .vqb-group::before {
  border-width: 0 0 1px 1px;
  height: 45px;
  top: -25px;
}

.vue-query-builder-styled .vqb-rule::after,
.vue-query-builder-styled .vqb-group::after {
  border-width: 0 0 0 1px;
  height: calc(100% - 17px);
  top: 17px;
}

.vue-query-builder-styled .vqb-rule:last-child::after,
.vue-query-builder-styled .vqb-group:last-child::after {
  border: 0;
}

.vue-query-builder-styled .dashed > .vqb-rule::after,
.vue-query-builder-styled .dashed > .vqb-rule::before,
.vue-query-builder-styled .dashed > .vqb-group::after,
.vue-query-builder-styled .dashed > .vqb-group::before {
  border-style: dashed;
}

.vue-query-builder-styled .vqb-rule {
  margin-top: 15px;
  margin-bottom: 15px;
  padding: 5px;
  border-radius: 4px;
}

.vue-query-builder-styled .vqb-group {
  padding: 5px;

  border-radius: 4px;
}

.vue-query-builder-styled .vqb-group.depth-1 .vqb-rule,
.vue-query-builder-styled .vqb-group.depth-2 {
  border-color: #5867dd;
  border-left: 2px solid #5867dd;
}

.vue-query-builder-styled .vqb-group.depth-2 .vqb-rule,
.vue-query-builder-styled .vqb-group.depth-3 {
  border-color: #1dc9b7;
  border-left: 2px solid #1dc9b7;
}

.vue-query-builder-styled .vqb-group.depth-3 .vqb-rule,
.vue-query-builder-styled .vqb-group.depth-4 {
  border-color: #fd27eb;
  border-left: 2px solid #fd27eb;
}

.vue-query-builder-styled .vqb-rule::after,
.vue-query-builder-styled .vqb-rule::before,
.vue-query-builder-styled .vqb-group::after,
.vue-query-builder-styled .vqb-group::before {
  border-color: inherit;
}

.vue-query-builder-styled .close {
  opacity: 1;
  color: rgb(150, 150, 150);
  position: relative;
  line-height: 38px;
}

.vue-query-builder-styled .form-group .close {
  left: 5px;
}

.vue-query-builder-styled .mycard-header .close {
  left: 5px;
}

@media (min-width: 768px) {
  .vue-query-builder-styled .vqb-rule.form-inline .form-group {
    display: block;
  }
}

.vue-query-builder-styled .vqb-label {
  display: inline-block;
  border: 1px solid #e2e5ec;
  border-radius: 0.25rem;
  height: calc(1.5em + 1.3rem + 2px);
  padding: 0.65rem 1rem;
  width: auto;
  vertical-align: middle;
  margin: 0;
}
</style>
